import axiosInstance from "./axios";

export interface MerchantTerms {
    [key: string]: string[];
}

export interface Merchant {
    id: string;
    name: string;
    domain: string;
    tnc_url?: string | null;
    logo_url?: string;
    terms?: MerchantTerms;
}

export interface Product {
    id: string;
    title: string;
    price: number | string;
    image_url: string;
    merchants: Merchant;
    variants?: any[];
    available_sizes?: string[];
    available_colors?: string[];
    price_range?: string;
    description?: string;
    min_price?: number;
    max_price?: number;
}

export interface GetMerchantProductCatalogueRequest {
    merchant_id?: string;
    merchant_slug?: string;
}

export interface GetMerchantProductCatalogueResponse {
    success: boolean;
    data: Product[];
    message: string;
}

export const getMerchantProductCatalogue = async (
    payload: GetMerchantProductCatalogueRequest
): Promise<GetMerchantProductCatalogueResponse> => {
    try {
        const response = await axiosInstance.post<GetMerchantProductCatalogueResponse>(
            "/api/products/getMerchantProductCatalouge",
            payload
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching merchant product catalogue:", error);
        throw error;
    }
};

export interface OnBoardCustomerRequest {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    product_id: string;
    merchant_id: string;
    otp: string;
}

export interface OnBoardCustomerResponse {
    success: boolean;
    message: string;
    token?: string; // Present if user already exists
}

export const onBoardCustomerThroughQrCode = async (
    payload: OnBoardCustomerRequest
): Promise<OnBoardCustomerResponse> => {
    try {
        const response = await axiosInstance.post<OnBoardCustomerResponse>(
            "/api/customer/onboard_customer_through_qr_code",
            payload
        );
        return response.data;
    } catch (error) {
        console.error("Error onboarding customer via QR code:", error);
        throw error;
    }
};

export interface SendOtpForQrRegistrationRequest {
    email: string;
}

export interface SendOtpForQrRegistrationResponse {
    success: boolean;
    message: string;
}

export const sendOtpForQrRegistration = async (
    payload: SendOtpForQrRegistrationRequest
): Promise<SendOtpForQrRegistrationResponse> => {
    try {
        const response = await axiosInstance.post<SendOtpForQrRegistrationResponse>(
            "/api/customer/send_otp_to_customer_for_qr_registration",
            payload
        );
        return response.data;
    } catch (error) {
        console.error("Error sending OTP for QR registration:", error);
        throw error;
    }
};

// ── Magic Link ──────────────────────────────────────────────

export interface MagicLinkMerchantPricing {
    months12: number;
    months24: number;
    shipping: number;
}

export interface MagicLinkExtendedPricing {
    months12: number;
    months24: number;
    initial_warranty_month: number;
}

export interface MagicLinkCustomerAddress {
    province_code: string;
    city?: string;
    country?: string;
    name?: string;
}

export interface MagicLinkCustomerData {
    id: string;
    address: MagicLinkCustomerAddress | null;
}

export interface MagicLinkMerchant {
    id: string;
    name: string;
    logo_url: string | null;
    terms: MerchantTerms | string | null;
    domain: string;
    pricing: MagicLinkMerchantPricing;
    extended_pricing: MagicLinkExtendedPricing;
}

export interface MagicLinkPolicyPricing {
    [months: string]: number | null;  // "12", "24", "36", etc.
}

export interface MagicLinkProduct {
    id: string;
    title: string;
    image_url: string;
    price: number;
    policyType: string;           // "extended", "accidental", etc.
    policyPricing: MagicLinkPolicyPricing;
}

export interface MagicLinkOrdersData {
    id: string;
    product_id: string[];
    merchant_id: string;
}

export interface MagicLinkContentsResponse {
    success: boolean;
    message: string;
    data: {
        leadsId: string;
        ordersData: MagicLinkOrdersData;
        merchant: MagicLinkMerchant;
        productsArray: MagicLinkProduct[];
        customerData?: MagicLinkCustomerData[];
    };
}

export const getMagicLinkContents = async (
    magicToken: string
): Promise<MagicLinkContentsResponse> => {
    try {
        const response = await axiosInstance.post<MagicLinkContentsResponse>(
            "/api/policy/getMagicLinkContents",
            { magicToken }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching magic link contents:", error);
        throw error;
    }
};

// ── Razorpay ────────────────────────────────────────────────

export interface BuyPolicyViaLeadsRequest {
    db_order_id: string;
    lead_id: string;
    merchant_id: string;
    product_id: string;
    policy_type: string;     // "extended", "accidental", etc.
    policy_term: number;     // in months e.g. 12, 24
    quantity: number;
    customer_id: string;
    customer_state: string;  // e.g. "MH"
}

export interface BuyPolicyViaLeadsResponse {
    message: string;
    razorpayOrderID: string;
    razorpayOrderAmount: number;
    leads_transaction_id: string;
}

export const buyPolicyViaLeads = async (
    payload: BuyPolicyViaLeadsRequest
): Promise<BuyPolicyViaLeadsResponse> => {
    try {
        const response = await axiosInstance.post<BuyPolicyViaLeadsResponse>(
            "/api/policy/buyPolicyViaLeads",
            payload
        );
        return response.data;
    } catch (error) {
        console.error("Error buying policy via leads:", error);
        throw error;
    }
};

export interface CheckPaymentStatusRequest {
    leads_transaction_id: string;
}

export interface CheckPaymentStatusResponse {
    success: boolean;
    message: string;
    data?: {
        razorpay_status: string | null;
        paid: boolean;
    };
}

export const checkPaymentStatus = async (
    payload: CheckPaymentStatusRequest
): Promise<CheckPaymentStatusResponse> => {
    try {
        const response = await axiosInstance.post<CheckPaymentStatusResponse>(
            "/api/policy/checkStatus",
            payload
        );
        return response.data;
    } catch (error) {
        console.error("Error checking payment status:", error);
        throw error;
    }
};
